import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSecondResults, getAccommodationPreferences, createOrUpdateAccommodationPreference, updateUserFlag } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Switch from "@/components/ui/Switch"; // 修正: デフォルトエクスポートをインポート
import { Hotel, ArrowLeftRight, Info, CheckCircle2, HelpCircle, Loader2 } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import OnboardingTour from './OnboardingTour';
import '../styles/SecondResult.css';

// チュートリアルのステップ定義
const TOUR_STEPS = [
  {
    target: '[data-tour="second-result-card"]',
    title: '2次抽選結果の確認',
    content: 'このページでは、2次抽選の結果を確認し、各クールの宿泊希望を設定できます。',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="result-list"]',
    title: '配属結果一覧',
    content: 'ここに各クールの配属先が表示されています。クール、配属機関、宿泊希望の3列で構成されています。',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour="accommodation-toggle"]',
    title: '宿泊希望の設定',
    content: 'このスイッチで宿泊を希望するかどうかを設定します。クリックして切り替えてください。',
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 5,
  },
  {
    target: '[data-tour="submit-button"]',
    title: '宿泊希望を送信',
    content: '宿泊希望の設定が完了したら、このボタンを押して送信してください。交換を希望する場合も必ず送信してください。',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour="exchange-button"]',
    title: '病院交換希望申請',
    content: '宿泊希望の設定を完了した後、配属先の交換を希望する場合は、こちらから申請ページに進めます。',
    placement: 'top',
    disableBeacon: true,
  },
];

const SecondResult = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [results, setResults] = useState([]);
  const [accommodationChoices, setAccommodationChoices] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      fetchSecondResults();
    }
  }, [user, navigate]);

  const fetchSecondResults = async () => {
    try {
      const secondResults = await getSecondResults();
      setResults(secondResults);

      // 宿泊希望の取得
      const preferences = await getAccommodationPreferences();
      const initialChoices = {};
      preferences.forEach(pref => {
        initialChoices[pref.assignment_id] = pref.accommodation;
      });

      // 宿泊希望が設定されていない場合、デフォルト値を設定
      secondResults.forEach(result => {
        if (!(result.assignment_id in initialChoices)) {
          initialChoices[result.assignment_id] = true; // デフォルト値
        }
      });

      setAccommodationChoices(initialChoices);
    } catch (err) {
      console.error('結果の取得に失敗しました:', err);
      setError('結果の取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccommodationChange = (assignmentId, value) => {
    setAccommodationChoices({
      ...accommodationChoices,
      [assignmentId]: value,
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      for (const assignmentId in accommodationChoices) {
        await createOrUpdateAccommodationPreference(assignmentId, accommodationChoices[assignmentId]);
      }

      if (user && user.uid) {
        await updateUserFlag(user.uid, 'second_result_status', true);
      }

      alert('宿泊の希望が更新されました。');
    } catch (err) {
      console.error('宿泊の希望の更新に失敗しました:', err);
      setError('宿泊の希望の更新に失敗しました。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExchangeClick = () => {
    navigate('/exchange');
  };

  if (!user) return null;
  if (isLoading) return (
    <div className="flex justify-center items-center h-screen bg-background">
      <LoadingSpinner text="結果を読み込んでいます..." />
    </div>
  );
  if (error) return (
    <div className="flex justify-center items-center h-screen bg-background">
      <div className="text-accent text-xl">{error}</div>
    </div>
  );

  return (
    <div className="second-result-container">
      {/* オンボーディングツアー */}
      <OnboardingTour
        tourKey="second-result"
        steps={TOUR_STEPS}
        isReady={!isLoading && results.length > 0}
      />

      <Card className="dashboard-card full-width" data-tour="second-result-card">
        <CardHeader className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', width: '100%' }}>
            <div>
              <CardTitle className="card-title">
                2次抽選結果確認画面
              </CardTitle>
              <CardDescription className="card-description">
                宿泊の希望を選択した後、交換・日時変更申請を行ってください。
              </CardDescription>
            </div>
            <button
              type="button"
              onClick={() => {
                try {
                  localStorage.removeItem('tour-completed-second-result');
                } catch { }
                window['second-resultTour']?.startTour?.();
              }}
              title="操作ガイドを再表示"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                color: '#1e3a5f',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
              }}
            >
              <HelpCircle size={18} />
              <span>操作ガイド</span>
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {results.length === 0 ? (
            <div className="empty-state">
              <Info size={40} />
              <p>現在、割り当てられた結果がありません。</p>
            </div>
          ) : (
            <>
              <div className="result-list-container" data-tour="result-list">
                <div className="result-header-row">
                  <div className="header-cell cycle-col">クール</div>
                  <div className="header-cell institution-col">配属機関</div>
                  <div className="header-cell accommodation-col">宿泊希望</div>
                </div>
                {results.map((result, index) => (
                  <div key={index} className="result-item-row">
                    <div className="cell cycle-col">
                      <span className="cycle-badge">第{result.term_id}クール</span>
                    </div>
                    <div className="cell institution-col">
                      <div className="institution-name">{result.institution_name}</div>
                      <div className="department-name">{result.department_name}</div>
                    </div>
                    <div className="cell accommodation-col" data-tour={index === 0 ? 'accommodation-toggle' : undefined}>
                      <label className={`accommodation-toggle ${accommodationChoices[result.assignment_id] ? 'active' : ''}`}>
                        <Switch
                          checked={accommodationChoices[result.assignment_id] || false}
                          onCheckedChange={(value) => handleAccommodationChange(result.assignment_id, value)}
                        />
                        <span className="toggle-label">
                          {accommodationChoices[result.assignment_id] ? (
                            <><Hotel size={16} /> 宿泊する</>
                          ) : (
                            '宿泊しない'
                          )}
                        </span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>

              <div className="action-buttons">
                <Button
                  onClick={handleSubmit}
                  className="submit-button"
                  data-tour="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      送信中...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={18} className="mr-2" />
                      宿泊希望を送信
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleExchangeClick}
                  variant="outline"
                  className="exchange-button-outline"
                  data-tour="exchange-button"
                >
                  <ArrowLeftRight size={18} className="mr-2" />
                  病院交換希望申請へ進む
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SecondResult;
